import { useUserStore } from '@/stores/system/user'
import type { NavigationGuard, RouteLocationNormalized } from 'vue-router'
import { type RouteRecordRaw, useRouter } from 'vue-router'
import { useModal } from 'composables/useModal'
import { isCypress } from '@/helpers/isCypress'
import { aliases, convertAliasesToRegex } from './aliases'

// import.meta.env.DEV is Vite's reliable dev flag; process.env.NODE_ENV is
// undefined in the client bundle, which silently disabled the dev-skip below.
const isDevBuild = import.meta.env.DEV

/**
 * Validates if a route parameter 'id' is a valid number
 */
const itemIsNaN = (to: RouteLocationNormalized): boolean => {
	if (!Object.prototype.hasOwnProperty.call(to, 'params')) {
		throw new TypeError(`One must define params for ${String(to.name)}`)
	}
	if (!Object.prototype.hasOwnProperty.call(to.params, 'id')) {
		throw new TypeError(
			`One must define an id in params for ${String(to.name)}`
		)
	}

	return isNaN(parseInt(to.params.id as string))
}

/**
 * Guard to ensure item ID parameters are valid numbers
 */
export const itemNaNGuard: NavigationGuard = (to) => {
	const { matched } = to

	if (matched.some((record) => record.meta?.validateItem)) {
		if (itemIsNaN(to)) {
			return {
				name: 'error',
				params: { id: 404 },
			}
		}
	}

	return true
}

/**
 * Guard to ensure the route exists, redirect to 404 if not
 */
export const ensureRouteExists: NavigationGuard = (to) => {
	if (to.matched.length > 0) {
		return true
	}

	const router = useRouter()
	const routeExists = router.resolve(to.fullPath)

	if (routeExists?.matched.some((record) => record.meta?.auth)) {
		return { path: '/', query: { redirect: to.fullPath } }
	}

	return { name: 'error', params: { id: 404, path: to.fullPath } }
}

/**
 * Guard to ensure user is authenticated for protected routes
 */
export const loginGuard: NavigationGuard = (to) => {
	const userStore = useUserStore()
	const { isLoggedIn } = userStore

	// Check if route requires authentication
	if (to.matched.some((record) => record.meta?.auth) && !isLoggedIn) {
		return {
			path: '/',
			query: { redirect: to.fullPath },
		}
	}

	return true
}

/**
 * Guard to ensure user has accepted terms and privacy policy
 */
export const policyGuard: NavigationGuard = (to) => {
	const { isAdmin, isTeam, isLoggedIn, whoami } = useUserStore()

	const liaValue = sessionStorage.getItem('lia')
	const hasNoLia =
		!liaValue ||
		liaValue === '{}' ||
		liaValue === '' ||
		liaValue === 'undefined'

	const shouldCheckPolicy =
		isLoggedIn &&
		hasNoLia &&
		!isAdmin &&
		!isTeam &&
		(!isDevBuild || isCypress()) &&
		to.name !== 'sitepolicy'

	if (shouldCheckPolicy && whoami && (!whoami.tos || !whoami.privacy)) {
		return { name: 'sitepolicy' }
	}

	return true
}

/**
 * Guard to remove trailing slashes from URLs
 */
export const removeTrailingSlashGuard: NavigationGuard = (to) => {
	if (to.fullPath.length > 1 && to.fullPath.endsWith('/')) {
		return { path: to.fullPath.replace(/\/$/, '') }
	}
	return true
}

/**
 * Recursively search for a route by name or path in route tree
 */
export const recursiveChildrenSearch = (
	routes: RouteRecordRaw[],
	name: string,
	path = ''
): RouteRecordRaw | undefined => {
	if (!routes) return undefined

	let result = routes.find(
		(route) =>
			route.meta?.name === name ||
			route.name === name ||
			(route.path === path && path.length >= 1)
	)

	if (result) return result

	for (const route of routes) {
		if (route?.children) {
			result = recursiveChildrenSearch(route.children, name)
			if (result) return result
		}
	}

	return undefined
}

/**
 * Guard to handle modal routes
 * Allow URL to change, handle modal state separately
 */
export const modalGuard: NavigationGuard = (to, from) => {
	let queryParams = undefined
	if (from.query.modal) {
		const { removeModalParams } = useModal()
		queryParams = removeModalParams(from.query, from.query.modal as string)
	}
	if (!to.meta.modal) {
		return true
	}
	let params = to.params
	if (to.meta.params) {
		params = { ...params, ...to.meta.params, ...to.query }
	}
	if (to.query) {
		params = { ...params, ...to.query }
	}
	const { openModal, closeAllModals } = useModal()
	closeAllModals()
	openModal(
		to.name?.toString() as string,
		{ ...params, origin: from.fullPath },
		{},
		queryParams
	)
}

/**
 * Guard to handle route aliases
 */
export const aliasGuard: NavigationGuard = (to) => {
	const aliasesAsArray = Object.entries(convertAliasesToRegex(aliases))

	const alias = aliasesAsArray.find(([, patterns]) => {
		return patterns.some((pattern) => new RegExp(pattern).test(to.fullPath))
	})

	if (alias?.[0] === 'user_report') {
		return {
			name: 'profile',
			query: {
				modal: 'reportView',
				id: to.params.id as string,
				origin: '/profile',
			},
		}
	}

	return true
}
